import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface IElementViewerVisibility {
    title?: boolean;
    description?: boolean;
    wall?: boolean;
    extLink?: boolean;
    actionsBar?: boolean;
    iNotification?: boolean;
    iOpenExt?: boolean;
    bookmark?: boolean;
    segment?: boolean;
    eFilters?: boolean;
    summary?: boolean;
    control_buttons?: {
        info?: boolean;
        favorite?: boolean;
        download?: boolean;
        open_external_report?: boolean;
        full_screen?: boolean;
        edit?: boolean;
    };
    action_select?: boolean;
    action_select_buttons?: {
        share?: boolean;
        rating?: boolean;
        lineage?: boolean;
        expert_analysis?: boolean;
        notification?: boolean;
    };
    collaboration_controls?: {
        chart_overlay?: boolean;
        add_event?: boolean;
        add_annotation?: boolean;
    };
}

export interface IElementViewerDimensions {
    height?: number;
    width?: number;
}

export interface IElementViewerStyles {
    [key: string]: any;
}

export interface IElementViewerCustomEvents {
    external_link_click?: boolean;
    favorite_click?: boolean;
}

export interface IElementViewerContentDimensions {
    ignoreExtReportCalcByImgAspectRatio?: boolean;
}

const defaultVisibility: IElementViewerVisibility = {
    title: true,
    description: true,
    wall: true,
    extLink: true,
    actionsBar: true,
    iNotification: true,
    iOpenExt: true,
    bookmark: true,
    segment: true,
    eFilters: true,
    summary: true,
    control_buttons: {
        info: true,
        favorite: true,
        download: true,
        open_external_report: true,
        full_screen: true,
        edit: true,
    },
    action_select: true,
    action_select_buttons: {
        share: true,
        rating: true,
        lineage: true,
        expert_analysis: true,
        notification: true,
    },
    collaboration_controls: {
        chart_overlay: true,
        add_event: true,
        add_annotation: true,
    },
};

export const elementViewerSlice = createSlice({
    name: 'elementViewer',
    initialState: {
        visibility: defaultVisibility,
        isApp: false,
        dimensions: {
            height: undefined,
            width: undefined,
        } as IElementViewerDimensions,
        styles: {} as IElementViewerStyles,
        customEvents: {} as IElementViewerCustomEvents,
        contentDimensions: {} as IElementViewerContentDimensions,
    },
    reducers: {
        setElementViewerVisibility: (state, action: PayloadAction<IElementViewerVisibility>) => {
            state.visibility = { ...defaultVisibility, ...action.payload };
        },
        updateElementViewerVisibility: (state, action: PayloadAction<Partial<IElementViewerVisibility>>) => {
            state.visibility = { ...state.visibility, ...action.payload };
        },
        resetElementViewerVisibility: (state) => {
            state.visibility = defaultVisibility;
        },
        setIsApp: (state, action: PayloadAction<boolean>) => {
            state.isApp = action.payload;
        },
        setElementViewerDimensions: (state, action: PayloadAction<IElementViewerDimensions>) => {
            state.dimensions = action.payload;
        },
        updateElementViewerDimensions: (state, action: PayloadAction<Partial<IElementViewerDimensions>>) => {
            state.dimensions = { ...state.dimensions, ...action.payload };
        },
        setElementViewerStyles: (state, action: PayloadAction<IElementViewerStyles>) => {
            state.styles = action.payload;
        },
        updateElementViewerStyles: (state, action: PayloadAction<Partial<IElementViewerStyles>>) => {
            state.styles = { ...state.styles, ...action.payload };
        },
        setElementViewerCustomEvents: (state, action: PayloadAction<IElementViewerCustomEvents>) => {
            state.customEvents = action.payload;
        },
        updateElementViewerCustomEvents: (state, action: PayloadAction<Partial<IElementViewerCustomEvents>>) => {
            state.customEvents = { ...state.customEvents, ...action.payload };
        },
        resetElementViewerConfig: (state) => {
            state.visibility = defaultVisibility;
            state.isApp = false;
            state.dimensions = { height: undefined, width: undefined };
            state.styles = {};
            state.customEvents = {};
        },
        setElementViewerContentDimensions: (state, action: PayloadAction<IElementViewerContentDimensions>) => {
            state.contentDimensions = action.payload;
        },
    },
});

export const {
    setElementViewerVisibility,
    updateElementViewerVisibility,
    resetElementViewerVisibility,
    setIsApp,
    setElementViewerDimensions,
    updateElementViewerDimensions,
    setElementViewerStyles,
    updateElementViewerStyles,
    setElementViewerCustomEvents,
    updateElementViewerCustomEvents,
    resetElementViewerConfig,
    setElementViewerContentDimensions,
} = elementViewerSlice.actions;

export default elementViewerSlice.reducer;
